import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormDatepicker } from '@ipfabric/ui-components/FormDatepicker/FormDatepicker';
import { FormIPSubnetField } from '@ipfabric/ui-components/FormIPSubnetField/FormIPSubnetField';
import { FormSwitch } from '@ipfabric/ui-components/FormSwitch/FormSwitch';
import { FormTextArea } from '@ipfabric/ui-components/FormTextArea/FormTextArea';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import {
  type IPSubnetInputValueOrDraft,
  hasIPSubnetInputValueError,
  ipSubnetInputValueSchema,
} from '@ipfabric/ui-components/IPSubnetInput/IPSubnetInput.types';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { z } from 'zod';

interface EditEnablePwdDialogueProps {
  onClose: () => void;
  formType: 'add' | 'edit';
  onSubmit: (values: EditEnablePwdFormValues) => void;
  initialValues?: EditEnablePwdFormValues;
}

export interface EditEnablePwdFormValues {
  username: string;
  password: string;
  retypePassword: string;
  expirationEnabled: boolean;
  expirationValue: Date | null;
  includeNetworks: IPSubnetInputValueOrDraft;
  excludeNetworks: IPSubnetInputValueOrDraft;
  notes: string;
}

export const EditEnablePwdDialogue = ({
  onClose,
  formType,
  onSubmit,
  initialValues,
}: EditEnablePwdDialogueProps) => {
  const { handleSubmit, formState, control, setValue } =
    useCustomForm<EditEnablePwdFormValues>({
      defaultValues: (() => {
        const hasExpirationValue = !!initialValues?.expirationValue;

        return {
          username: initialValues?.username ?? '',
          password: initialValues?.password ?? '',
          retypePassword: initialValues?.retypePassword ?? '',
          expirationEnabled: hasExpirationValue,
          expirationValue: initialValues?.expirationValue ?? null,
          includeNetworks: initialValues?.includeNetworks ?? { ips: [] },
          excludeNetworks: initialValues?.excludeNetworks ?? { ips: [] },
          notes: initialValues?.notes ?? '',
        } satisfies EditEnablePwdFormValues;
      })(),
      resolver: zodResolver(getValidationSchema(formType)),
    });

  const expirationEnabled = useWatch({
    control,
    name: 'expirationEnabled',
  });
  const expirationValue = useWatch({
    control,
    name: 'expirationValue',
  });

  useEffect(() => {
    setValue('expirationEnabled', !!expirationValue);
  }, [expirationValue, setValue]);

  return (
    <SidePanel
      title={
        formType === 'add' ? 'Add enable password' : 'Edit enable password'
      }
      closeOnBackdropClick={false}
      onClose={onClose}
      footer={({ closeModal }) => (
        <div className='flex gap-xs'>
          <Button
            className='flex-1'
            dataTestId='privileges-save'
            disabled={!formState.isDirty}
            icon='fa-floppy-disk'
            loading={formState.isSubmitting}
            onClick={handleSubmit(onSubmit)}
            type='submit'
            variant='primary'
          >
            Save
          </Button>
          <Button className='flex-1' variant='outline' onClick={closeModal}>
            Close
          </Button>
        </div>
      )}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card className='flex flex-col gap p-sm'>
          <FormTextField
            control={control}
            disabled={formType === 'edit'}
            name='username'
            label='Username'
            type='text'
            className='flex-1'
          />
          <FormTextField
            control={control}
            name='password'
            label='Password'
            type='password'
            className='flex-1'
          />
          <FormTextField
            control={control}
            name='retypePassword'
            label='Retype password'
            type='password'
            className='flex-1'
          />
          <FormIPSubnetField
            control={control}
            name='includeNetworks'
            label='Use in subnets'
            type='cidr'
          />
          <FormIPSubnetField
            control={control}
            name='excludeNetworks'
            label='Don’t use in subnets'
            type='cidr'
          />
          <div className='flex flex-col gap-xs'>
            <FormSwitch
              control={control}
              name='expirationEnabled'
              description={'Set expiration date'}
              className='flex-1'
            />
            {expirationEnabled && (
              <FormDatepicker
                className='ml-12'
                control={control}
                name='expirationValue'
              />
            )}
          </div>
          <FormTextArea control={control} name='notes' label='Notes' />
        </Card>
      </form>
    </SidePanel>
  );
};

const getValidationSchema = (formType: 'add' | 'edit') => {
  return z
    .object({
      username: z.string().optional(),
      expirationEnabled: z.boolean(),
      expirationValue: z.date().nullable(),
      includeNetworks: ipSubnetInputValueSchema,
      excludeNetworks: ipSubnetInputValueSchema,
      notes: z.string(),
      password:
        formType === 'add'
          ? z.string().min(1, { message: 'Password is required' })
          : z.string().optional(),
      retypePassword:
        formType === 'add'
          ? z.string().min(1, { message: 'Please confirm your password' })
          : z.string().optional(),
    })
    .refine(
      (data) => {
        if (formType === 'edit' && !data.password && !data.retypePassword) {
          return true;
        }
        return data.password === data.retypePassword;
      },
      {
        message: "Passwords don't match",
        path: ['retypePassword'], // Specify the path for the error
      },
    )
    .superRefine((data, ctx) => {
      if (hasIPSubnetInputValueError(data.includeNetworks)) {
        // Display the error message from the draft value
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: data.includeNetworks.message,
          path: ['includeNetworks'],
        });
      }

      if (hasIPSubnetInputValueError(data.excludeNetworks)) {
        // Display the error message from the draft value
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: data.excludeNetworks.message,
          path: ['excludeNetworks'],
        });
      }
    });
};
